<script setup lang="ts">
	//import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
	import { useSettings } from "@/composables/settings";
	import {
		attachIotPolicy,
		configureAWS,
		forceMqttReconnect,
		getMqttJsClient,
	} from "@ptx-autonomous/libs/api";
	import { initOpenGlClient } from "@ptx-autonomous/libs/opengl-api";
	import { useGlobalFailoverHook } from "@shared/composables/global-failover-hook";
	import {
		getApiUrl,
		getAwsConfiguration,
	} from "@shared/configuration/aws-configuration";
	import { getAwsExports } from "@shared/configuration/aws-exports";
	import { clearCredentials } from "@aws-amplify/core";
	import { fetchAuthSession } from "aws-amplify/auth";

	const { environment, apiVersion } = useSettings();

	// Fires only when a poll of status.json shows the region actually changed
	// (not on every 30s tick - see useGlobalFailoverHook). The main api-v2
	// client uses initClientReactive, so it already re-points itself once the
	// region resolver's internal state changes - nothing to do for it here.
	useGlobalFailoverHook(environment, async newRegion => {
		const startedAt = Date.now();
		const awsConfig = getAwsConfiguration(environment.value);
		console.info(
			`[failover] Active region changed to "${newRegion}" - reapplying AWS config.`,
		);

		configureAWS(getAwsExports(environment.value));
		initOpenGlClient(getApiUrl(environment.value, apiVersion.value));

		// configureAWS() only updates Amplify's *configuration* - it doesn't
		// clear the credentials AND identity ID Amplify already has cached in
		// memory from the old region's Cognito Identity Pool. An Identity ID is
		// only valid within the identity pool that minted it, so reusing the
		// old one against the new pool fails with
		// "ResourceNotFoundException: Identity '...' not found" (seen as a 400
		// from cognito-identity, then IoT Core's "Not authorized" on MQTT
		// connect). clearCredentials() wipes both, forcing a brand new identity
		// to be minted against the newly-configured pool on the next
		// fetchAuthSession call below. Must happen after configureAWS (so the
		// next fetch targets the new identity pool) and before anything below
		// that needs credentials.
		let session: Awaited<ReturnType<typeof fetchAuthSession>> | undefined;
		try {
			await clearCredentials(); // wipes cached credentials + Identity ID from the old region's pool
			session = await fetchAuthSession({ forceRefresh: true });
			console.info(
				`[failover] Refreshed AWS credentials for the new region (idToken present: ${!!session.tokens?.idToken}).`,
			);
		} catch (error) {
			console.error(
				"[failover] Failed to refresh AWS credentials for the new region:",
				error,
			);
		}

		try {
			// Call IoT policy attached which this may not be called yet on the failover region
			if (session?.tokens?.idToken) {
				await attachIotPolicy(
					getApiUrl(environment.value, apiVersion.value),
				);
				console.info(
					"[failover] Attached IoT policy for the new region.",
				);
			} else {
				console.info(
					"[failover] Skipping IoT policy attach - no idToken available.",
				);
			}
		} catch (error) {
			console.error(
				"[failover] Failed to attach IoT policy for the new region:",
				error,
			);
		}

		// Amplify PubSub-backed cloud connection: forceMqttReconnect rebuilds it
		// against the new region/host and resubscribes everything already
		// listening - built for exactly this.
		forceMqttReconnect(awsConfig.region, awsConfig.host);

		try {
			// reconnect() keeps the same shared client object (and every existing
			// subscription registered on it) and just redirects its connection to
			// the new region's host - unlike resetSharedMqttJsClient() +
			// getSharedMqttJsClient(), which would replace the client object and
			// silently orphan every caller (e.g. useMonitorMachines) still holding
			// a reference to the old one.
			await getMqttJsClient().reconnect({
				type: "aws",
				host: awsConfig.host,
				region: awsConfig.region,
				protocolVersion: 5,
				clientIdPrefix: "portal-mqtt",
			});
		} catch (error) {
			console.error(
				"[failover] Failed to reconnect the MQTT client for the new region:",
				error,
			);
		}

		// reconnect() only waits for the connection attempt to be kicked off,
		// not for it to actually finish connecting - this measures that setup
		// work, not full MQTT readiness.
		console.info(
			`[failover] Applied region "${newRegion}" in ${Date.now() - startedAt}ms (MQTT reconnect may still be in progress).`,
		);
	});
</script>

<template>
	<router-view />
	<!-- <VueQueryDevtools /> -->
</template>

<style>
	html {
		overflow-y: hidden;
	}

	.dp__theme_light {
		--dp-background-color: var(--color-green-50) !important;
		--dp-text-color: var(--color-grey-750) !important;
		--dp-primary-color: var(--color-green-600) !important;
		--dp-primary-disabled-color: var(--color-grey-150) !important;
	}
	.dp__calendar_header_item {
		color: var(--color-green-950);
	}

	.dp__action_buttons .dp__action_select {
		background: var(--dp-primary-color);
		color: var(--dp-primary-text-color);
	}

	.dp__input_icons {
		color: var(--color-green-600) !important;
	}
	/* Target the navigation control container */
	.maplibregl-ctrl-group .maplibregl-ctrl-compass,
	.maplibregl-ctrl-group .maplibregl-ctrl-zoom-in,
	.maplibregl-ctrl-group .maplibregl-ctrl-zoom-out {
		background-color: var(--color-green-600) !important;
		border-radius: 4px;
	}
	/* Style the zoom-in button icon */
	.maplibregl-ctrl-zoom-in {
		background-image: url("assets/ZoomIn.svg") !important;
		background-size: 20px 20px;
		background-repeat: no-repeat;
		background-position: center;
	}
	.maplibregl-ctrl-zoom-out {
		background-image: url("assets/ZoomOut.svg") !important;
		background-size: 20px 20px;
		background-repeat: no-repeat;
		background-position: center;
	}
	/* Remove the default icon if needed */
	.maplibregl-ctrl-zoom-in .maplibregl-ctrl-icon,
	.maplibregl-ctrl-zoom-out .maplibregl-ctrl-icon {
		display: none !important;
	}
	/* Change control group background */
	.maplibregl-ctrl-group {
		background: rgba(255, 255, 255, 0.9);
		padding: 2px;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}
</style>
